import '../../../theme/datepicker.css'

import styled from '@emotion/styled'
import { LogoSmall } from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { theme } from 'app/theme'
import { useVocabularyData } from 'app/utils/vocabulary'
import { enUS, it } from 'date-fns/esm/locale'
import { rgba } from 'emotion-rgba'
import { Link } from 'gatsby'
import React, { memo, useEffect, useRef, useState } from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import NumericInput from 'react-numeric-input'

registerLocale('en', enUS)
registerLocale('it', it)

export interface Props {
  beLanguage?: string
  languageCode: string
  languagePrefix: string
  modalStatus?: boolean
  siteName?: string
}

export const Syncrobox = memo(function Syncrobox({
  beLanguage,
  languageCode,
  languagePrefix,
  modalStatus,
  siteName,
}: Props) {
  const [startDate, setStartDate] = useState(new Date())
  const [endDate, setEndDate] = useState<any>(
    new Date(new Date().setDate(startDate.getDate() + 1)),
  )

  const guestsWrap = useRef(null)
  const [guestsWrapStatus, setGuestsWrapStatus] = useState(false)

  const [guestsTotal, setGuestsTotal] = useState('')
  const [adultsValue, setAdultsValue] = useState(2)
  const [childsValue, setChildsValue] = useState(0)

  const adultsLabel = useVocabularyData('adults', languageCode)
  const childsLabel = useVocabularyData('childs', languageCode)

  const handleClickOutside = (e: any) => {
    // @ts-ignore
    if (guestsWrap.current && !guestsWrap.current.contains(e.target)) {
      setGuestsWrapStatus(false)
    } else {
      if (guestsWrapStatus && e.target.classList.contains('guests-button')) {
        setGuestsWrapStatus(false)
      } else {
        setGuestsWrapStatus(true)
      }
    }
  }
  useEffect(() => {
    const adults = adultsValue > 0 ? `${adultsValue} ${adultsLabel}` : ''
    const childs = childsValue > 0 ? ` + ${childsValue} ${childsLabel}` : ''

    setGuestsTotal(`${adults}${childs}`)

    document.addEventListener('click', handleClickOutside, true)
    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [guestsWrapStatus, adultsValue, childsValue, startDate, endDate])

  return (
    <Container className={modalStatus ? 'open' : undefined}>
      <Logo
        aria-label={siteName}
        to={languagePrefix ? `/${languagePrefix}` : '/'}
      >
        <LogoSmall />
      </Logo>

      <Head>
        <Claim>{useVocabularyData('book', languageCode)}</Claim>

        <Title
          dangerouslySetInnerHTML={{
            __html: useVocabularyData('syncrobox-title', languageCode),
          }}
        />

        <Description
          dangerouslySetInnerHTML={{
            __html: useVocabularyData('syncrobox-text', languageCode),
          }}
        />
      </Head>

      <Form
        className={modalStatus ? 'open' : undefined}
        action="https://reservations.verticalbooking.com/premium/index2.html"
        target="_blank"
      >
        <input type="hidden" name="lingua_int" value={beLanguage} />
        <input type="hidden" name="tot_camere" value="1" />
        <input type="hidden" name="id_stile" value="15720" />
        <input type="hidden" name="headvar" value="ok" />
        <input type="hidden" name="dc" value="4328" />
        <input type="hidden" name="id_albergo" value="20080" />

        <input
          type="hidden"
          name="gg"
          id="gg"
          value={startDate.toLocaleDateString(languageCode, {
            day: 'numeric',
          })}
        />
        <input
          type="hidden"
          name="mm"
          id="mm"
          value={startDate.toLocaleDateString(languageCode, {
            month: '2-digit',
          })}
        />
        <input
          type="hidden"
          name="aa"
          id="aa"
          value={startDate.toLocaleDateString(languageCode, {
            year: 'numeric',
          })}
        />
        <input
          type="hidden"
          name="ggf"
          id="ggf"
          value={endDate.toLocaleDateString(languageCode, {
            day: 'numeric',
          })}
        />
        <input
          type="hidden"
          name="mmf"
          id="mmf"
          value={endDate.toLocaleDateString(languageCode, {
            month: '2-digit',
          })}
        />
        <input
          type="hidden"
          name="aaf"
          id="aaf"
          value={endDate.toLocaleDateString(languageCode, {
            year: 'numeric',
          })}
        />
        <input type="hidden" name="tot_adulti" value={adultsValue} />
        <input type="hidden" name="tot_bambini" value={childsValue} />

        <Wrapper dial={4} row>
          <Field>
            <Label>{useVocabularyData('check-in', languageCode)}</Label>
            <DatePicker
              autoComplete="off"
              dateFormat="dd/MM/yyyy"
              locale={languagePrefix || 'it'}
              onChange={(date: any) => {
                setStartDate(date)
                setEndDate(new Date(date.getTime() + 1 * 24 * 60 * 60 * 1000))
              }}
              minDate={new Date()}
              selected={startDate}
            />
          </Field>
          <Field>
            <Label>{useVocabularyData('check-out', languageCode)}</Label>
            <DatePicker
              autoComplete="off"
              dateFormat="dd/MM/yyyy"
              locale={languagePrefix || 'it'}
              onChange={(date: any) => {
                setEndDate(date)
              }}
              minDate={new Date(startDate?.getTime() + 1 * 24 * 60 * 60 * 1000)}
              selected={endDate}
            />
          </Field>
          <Guests ref={guestsWrap}>
            <Field>
              <Label>
                {useVocabularyData('guests-and-rooms', languageCode)}
              </Label>
              <GuestsValue
                className={`guests-button${guestsWrapStatus ? ' open' : ''}`}
              >
                {guestsTotal}
              </GuestsValue>
            </Field>
            <GuestsWrap
              className={guestsWrapStatus ? 'open' : undefined}
              row
              space="between"
              wrap
            >
              <Field>
                <GuestLabel>{adultsLabel}</GuestLabel>
                <NumericInput
                  min={1}
                  max={10}
                  style={false}
                  value={adultsValue}
                  onChange={(num: any) => {
                    setAdultsValue(num)
                  }}
                />
              </Field>
              <Field>
                <GuestLabel>{childsLabel}</GuestLabel>
                <NumericInput
                  min={0}
                  max={30}
                  style={false}
                  value={childsValue}
                  onChange={(num: any) => {
                    setChildsValue(num)
                  }}
                />
              </Field>
            </GuestsWrap>
          </Guests>
        </Wrapper>

        <Button>
          <Lines />
          <Submit
            type="submit"
            value={useVocabularyData('book-now', languageCode)}
          />
        </Button>

        <Separator />

        <Promo>
          <Code
            name="generic_codice"
            placeholder={useVocabularyData('promo-code', languageCode)}
            type="text"
          />
        </Promo>
      </Form>
    </Container>
  )
})

const Logo = styled(Link)`
  width: 8.6875rem;
  height: 2.5rem;
  opacity: 0;
  position: absolute;
  top: 2.5rem;
  left: 50%;
  transform: translate(-50%, 1.875rem);
  z-index: 2;

  > svg {
    width: 100%;
    height: 100%;
    fill: ${({ theme }) => theme.colors.variants.neutralLight5};
  }
`

const Head = styled.div``

const Claim = styled.div`
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 0.75rem;
  font-weight: 300;
  letter-spacing: 0.15em;
  line-height: 0.8125rem;
  margin-bottom: 0.75rem;
  opacity: 0;
  text-transform: uppercase;
  transform: translateY(2.5rem);
`

const Title = styled.div`
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 2.25rem;
  font-weight: 500;
  line-height: 2.0625rem;
  opacity: 0;
  text-transform: uppercase;
  transform: translateY(2.5rem);
`

const Description = styled.div`
  max-width: 34.375rem;
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.375rem;
  margin: 1.5rem auto 0;
  opacity: 0;
  transform: translateY(2.5rem);
`

const Container = styled.div`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.variants.primaryDark};
  color: ${({ theme }) => theme.colors.variants.neutralLight5};
  padding: 9.625rem 1.875rem 0;
  position: absolute;
  top: 0;
  left: 0;
  text-align: center;
  &.open {
    ${Logo} {
      opacity: 1;
      transform: translate(-50%, 0);
      transition: 0.4s 0.2s ease-in-out;
    }
    ${Claim},
    ${Title},
    ${Description} {
      opacity: 1;
      transform: none;
      transition: 0.4s 0.4s ease-in-out;
    }
    ${Claim} {
      transition-delay: 0.2s;
    }
    ${Title} {
      transition-delay: 0.4s;
    }
    ${Description} {
      transition-delay: 0.5s;
    }
  }
`

const Form = styled.form`
  display: inline-block;
  opacity: 0;
  margin: 13vh auto 0;
  &.open {
    opacity: 1;
    transition: 0.3s 0.6s ease-in-out;
  }

  .react-datepicker {
    min-width: 21.5rem;
    font-family: ${({ theme }) => theme.fontFamily.paragraph};
    background-color: ${({ theme }) => theme.colors.variants.neutralDark3};
    color: ${({ theme }) => theme.colors.variants.neutralLight5};
  }

  .react-datepicker__portal {
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${rgba(theme.colors.variants.neutralLight5, 0.8)};
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 999;
  }

  .react-datepicker__current-month {
    font-family: ${({ theme }) => theme.fontFamily.heading};
  }

  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day:hover,
  .react-datepicker__day--selected {
    background-color: ${({ theme }) => theme.colors.variants.primaryLight};
    color: ${({ theme }) => theme.colors.variants.primaryDark};
  }

  .react-datepicker__day:focus {
    background-color: ${({ theme }) =>
      theme.colors.variants.primaryLight} !important;
    color: ${({ theme }) => theme.colors.variants.primaryDark} !important;
  }

  .react-datepicker__day--today {
    &:after {
      background: ${({ theme }) => theme.colors.variants.primaryLight};
    }
  }

  .react-datepicker__day--disabled {
    opacity: 0.3;
  }

  .react-datepicker-wrapper {
    input {
      font-family: ${({ theme }) => theme.fontFamily.paragraph};
      font-size: 1rem;
      font-weight: 300;
      color: ${rgba(theme.colors.variants.neutralLight5, 0.2)};
      line-height: 1.375rem;
      &.react-datepicker-ignore-onclickoutside {
        color: ${({ theme }) => theme.colors.variants.neutralLight5};
      }
    }
  }

  .react-numeric-input {
    display: flex;
    align-items: center;
    margin-left: auto;
    position: relative;
    input {
      width: 2.5rem;
      caret-color: transparent;
      color: ${({ theme }) => theme.colors.variants.primaryLight};
      font-family: ${({ theme }) => theme.fontFamily.paragraph};
      font-size: 1.125rem;
      font-weight: 400;
      line-height: 1.5rem;
      margin-left: auto;
      pointer-events: none;
    }
    b {
      width: 1.8125rem;
      height: 1.8125rem;
      background: ${({ theme }) => theme.colors.variants.neutralDark4};
      cursor: pointer;
      position: relative;
      text-align: center;
      transition: 0.1s ease-out;
      &:hover {
        background: ${({ theme }) => theme.colors.variants.primaryLight};
        &:before,
        &:after {
          background: ${({ theme }) => theme.colors.variants.primaryDark};
        }
      }
      &:before,
      &:after {
        content: '';
        background: ${({ theme }) => theme.colors.variants.neutralLight5};
        position: absolute;
        transition: 0.1s ease-out;
      }
      &:nth-of-type(1) {
        &:before {
          width: 0.875rem;
          height: 0.125rem;
          top: 0.8125rem;
          left: 0.4375rem;
        }
        &:after {
          width: 0.125rem;
          height: 0.875rem;
          top: 0.4375rem;
          left: 0.8125rem;
        }
      }
      &:last-of-type {
        margin-left: 0.125rem;
        &:before {
          width: 0.875rem;
          height: 0.125rem;
          top: 0.8125rem;
          left: 0.5rem;
        }
      }
    }
  }
`

const Wrapper = styled(FlexBox)``

const Field = styled.div`
  min-width: 21.5rem;
  border-bottom: 0.0625rem solid
    ${({ theme }) => theme.colors.variants.neutralLight5};
  color: ${({ theme }) => theme.colors.variants.neutralLight5};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  margin: 0 1.875rem;
  text-align: left;

  @media (max-width: 1439px) {
    min-width: 18.75rem;
  }
`

const Label = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralLight5};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 0.75rem;
  font-weight: 300;
  letter-spacing: 0.15em;
  line-height: 0.8125rem;
  margin-bottom: 0.375rem;
  text-transform: uppercase;
`

const Guests = styled.div`
  position: relative;
`

const GuestsValue = styled.div`
  cursor: pointer;
  background: url('/svg/arrow-down.svg') no-repeat right center;
  color: ${rgba(theme.colors.variants.neutralLight5, 0.2)};
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.375rem;
  padding: 0.8125rem 0 0.75rem;
  &.open {
    background-image: url('/svg/arrow-up.svg');
    color: ${({ theme }) => theme.colors.variants.neutralLight5};
  }
`

const GuestsWrap = styled(FlexBox)`
  max-width: 21.625rem;
  background: ${({ theme }) => theme.colors.variants.neutralDark3};
  opacity: 0;
  padding-bottom: 1.25rem;
  position: absolute;
  top: calc(100% - 0.0625rem);
  left: 1.5vw;
  visibility: hidden;
  z-index: -1;
  &.open {
    opacity: 1;
    visibility: visible;
    z-index: 3;
  }
  > div {
    display: flex;
    align-items: center;
    width: 100%;
    min-width: auto;
    height: auto;
    border: 0;
    color: ${({ theme }) => theme.colors.variants.neutralLight5};
    margin: 1.25rem auto 0;
    padding: 0 1.25rem;
  }
`

const GuestLabel = styled.div`
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 0.75rem;
  letter-spacing: 0.15em;
  line-height: 0.8125rem;
  text-transform: uppercase;
`

const Lines = styled.span`
  &:before,
  &:after {
    content: '';
    width: 0.0625rem;
    background: ${theme.colors.variants.neutralLight5};
    position: absolute;
    transition: 0.4s ease-in-out;
  }
  &:before {
    top: 0;
    right: -0.0625rem;
    bottom: 100%;
  }
  &:after {
    top: 100%;
    left: -0.0625rem;
    bottom: 0;
  }
`

const Submit = styled.input`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.variants.primaryLight};
  color: ${({ theme }) => theme.colors.variants.primaryDark};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.15em;
  line-height: 1.125rem;
  padding: 0 2.25rem;
  position: relative;
  text-align: center;
  text-transform: uppercase;
  transition: 0.3s ease-in-out;
  z-index: 1;
`

const Button = styled.div`
  display: inline-block;
  height: 3.5rem;
  margin: 6rem auto 0;
  position: relative;
  &:before,
  &:after {
    content: '';
    height: 0.0625rem;
    background: ${theme.colors.variants.neutralLight5};
    position: absolute;
    transition: 0.4s ease-in-out;
  }
  &:before {
    top: -0.0625rem;
    right: 100%;
    left: 0;
  }
  &:after {
    bottom: -0.0625rem;
    right: 0;
    left: 100%;
  }
  &:hover {
    &:before {
      right: -0.0625rem;
    }
    &:after {
      left: -0.0625rem;
    }
    ${Lines} {
      &:before {
        bottom: -0.0625rem;
      }
      &:after {
        top: -0.0625rem;
      }
    }
    ${Submit} {
      background: transparent;
      color: ${({ theme }) => theme.colors.variants.neutralLight5};
    }
  }
`

const Separator = styled.div``

const Promo = styled.div`
  display: inline-block;
  min-width: 9.6875rem;
  margin-top: 1.875rem;
  padding-bottom: 0.4375rem;
  position: relative;
  &:hover {
    &:after {
      right: 0;
    }
  }
  &:before,
  &:after {
    content: '';
    height: 0.0625rem;
    position: absolute;
    bottom: 0;
    left: 0;
  }
  &:before {
    width: 100%;
    background: ${({ theme }) => theme.colors.variants.neutralDark3};
  }
  &:after {
    background: ${({ theme }) => theme.colors.variants.neutralLight5};
    right: 100%;
    transition: 0.3s ease-in-out;
  }
`

const Code = styled.input`
  width: 100%;
  color: ${({ theme }) => theme.colors.variants.neutralLight5};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 0.6875rem;
  font-weight: 500;
  letter-spacing: 0.15em;
  line-height: 0.875rem;
  text-transform: uppercase;
`
